
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { JobStatusBadge } from "@/components/features/badges";
import { formatPrice, formatRelativeTime } from "@/lib/format";
import type { DeliveryJob } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { MapPin, Package, Bike, Car, CarTaxiFront, Truck, Clock, Star } from "lucide-react";

const vehicleIcon: Record<string, React.ReactNode> = {
  any: <Package className="h-3.5 w-3.5" />,
  motorcycle: <Bike className="h-3.5 w-3.5" />,
  car: <Car className="h-3.5 w-3.5" />,
  badgag: <CarTaxiFront className="h-3.5 w-3.5" />,
  pickup: <Truck className="h-3.5 w-3.5" />,
  truck: <Truck className="h-3.5 w-3.5" />,
  // Legacy — kept for backward compat with old jobs.
  bicycle: <Bike className="h-3.5 w-3.5" />,
};

export function JobCard({ job, onClick }: { job: DeliveryJob; onClick?: () => void }) {
  const { t } = useTranslation();
  const price = job.agreedPrice ?? job.customerOffer;

  return (
    <Card
      onClick={onClick}
      className={`cursor-pointer p-4 transition hover:border-primary/40 hover:shadow-sm ${
        onClick ? "" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{job.code}</span>
            <JobStatusBadge status={job.status} />
          </div>
          <p className="mt-1 line-clamp-1 font-semibold">{job.parcelDescription}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-gold">{formatPrice(price)}</p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex items-start gap-2 text-muted-foreground">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <span className="line-clamp-1">{t("job.from")}: {job.pickupAddress}</span>
        </div>
        <div className="flex items-start gap-2 text-muted-foreground">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <span className="line-clamp-1">{t("job.to")}: {job.dropoffAddress}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            {vehicleIcon[job.vehicleRequired] || <Package className="h-3.5 w-3.5" />}
            <span className="capitalize">{job.vehicleRequired}</span>
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatRelativeTime(job.createdAt)}
          </span>
        </div>
        {job.provider && (
          <div className="flex items-center gap-1">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="bg-primary/10 text-[9px] text-primary">
                {job.provider.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
            <span>{job.provider.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
